import Vue from 'vue';
import {ProfileLayout} from '@/components/common/profileUi/profileLayout.helper';
import {UserPreferences} from '@/interfaces/clientOnly/userPreferences.interface';

/**
 * Own card order of the profile pages (ProfileCardGrid, 3-column layout only), per logged-in user.
 *
 * localStorage (`vanilla.profileLayouts.v1.<userId>`) is the synchronous cache for the first paint; the user-login-handler
 * stores it at the user (`uiPreferences.profileLayouts`, merged per page) so it follows to other devices. The server
 * answer of `me/preferences` is applied by loadUserPreferences (notificationPreferences.helper) after the login: a stored
 * server value wins, otherwise layouts that only exist in this browser are taken over once. An old user-login-handler
 * drops the field – the layouts then simply stay in this browser.
 */
const KEY_PREFIX = 'vanilla.profileLayouts.v1.';

export const profileLayoutState = Vue.observable({
    userId: undefined as string | undefined,
    layouts: {} as Record<string, ProfileLayout>,
    /** me/preferences answered (then changes are also sent to the server) */
    serverSupported: false,
});

const userApi = async () => (await import('@/classes/api/user-login-handler.api')).UserLoginHandlerApi;

const isLayoutObject = (value: unknown): value is ProfileLayout => !!value && typeof value === 'object'
    && ['left', 'main', 'right'].every((column) => Array.isArray((value as Record<string, unknown>)[column]));

function cleanLayouts(value: unknown): Record<string, ProfileLayout> {
    const out: Record<string, ProfileLayout> = {};
    if (value && typeof value === 'object') {
        Object.entries(value as Record<string, unknown>).forEach(([page, layout]) => {
            if (isLayoutObject(layout)) {
                out[page] = {left: [...layout.left], main: [...layout.main], right: [...layout.right]};
            }
        });
    }
    return out;
}

function readLocal(userId: string | undefined): Record<string, ProfileLayout> {
    if (!userId) {
        return {};
    }
    try {
        return cleanLayouts(JSON.parse(localStorage.getItem(KEY_PREFIX + userId) || 'null'));
    } catch (e) {
        return {};
    }
}

function writeLocal(userId: string | undefined, layouts: Record<string, ProfileLayout>) {
    if (!userId) {
        return;
    }
    try {
        if (Object.keys(layouts).length) {
            localStorage.setItem(KEY_PREFIX + userId, JSON.stringify(layouts));
        } else {
            localStorage.removeItem(KEY_PREFIX + userId);
        }
    } catch (e) {
        // Storage blocked (private mode, policy): the order only lasts for this page view
    }
}

/** Switches the cache to the given user (no-op when already active) */
export function useProfileLayoutsOf(userId: unknown): void {
    const id = userId ? String(userId) : undefined;
    if (profileLayoutState.userId === id) {
        return;
    }
    profileLayoutState.userId = id;
    profileLayoutState.layouts = readLocal(id);
    profileLayoutState.serverSupported = false;
}

/** Stored layout of a page (raw, still to be normalized against the cards), undefined = default */
export function storedProfileLayout(page: string): ProfileLayout | undefined {
    return profileLayoutState.layouts[page];
}

/** Saves (or with null resets) the layout of a page: immediately in this browser, at the user when available */
export async function saveProfileLayout(page: string, layout: ProfileLayout | null): Promise<boolean> {
    const layouts = {...profileLayoutState.layouts};
    if (layout) {
        layouts[page] = {left: [...layout.left], main: [...layout.main], right: [...layout.right]};
    } else {
        delete layouts[page];
    }
    profileLayoutState.layouts = layouts;
    writeLocal(profileLayoutState.userId, layouts);
    if (!profileLayoutState.serverSupported) {
        return true;
    }
    return (await userApi()).updateMyPreferences({uiPreferences: {profileLayouts: {[page]: layout}}});
}

/** Applies the `me/preferences` answer after the login (null: endpoint not available) */
export function applyServerProfileLayouts(userId: unknown, server: UserPreferences | null): void {
    useProfileLayoutsOf(userId);
    profileLayoutState.serverSupported = !!server;
    if (!server) {
        return;
    }
    const stored = server.uiPreferences?.profileLayouts;
    if (stored && typeof stored === 'object') {
        profileLayoutState.layouts = cleanLayouts(stored);
        writeLocal(profileLayoutState.userId, profileLayoutState.layouts);
    } else if (Object.keys(profileLayoutState.layouts).length) {
        // first device with server support: take over the layouts of this browser
        const layouts = {...profileLayoutState.layouts};
        void userApi().then((api) => api.updateMyPreferences({uiPreferences: {profileLayouts: layouts}}));
    }
}
